import { useCallback, useState } from 'react';
import {
    ArrowLeftIcon,
    CheckCircleIcon,
    ExclamationTriangleIcon,
    LinkIcon,
    LinkSlashIcon,
    MagnifyingGlassIcon
} from '@heroicons/react/24/outline';
import {
    searchSparkCases,
    linkSparkCase,
    unlinkSparkCase,
    type SparkCaseMatch
} from '../../../../services/sparkSubmission';
import { usePatientCard } from '../../../../hooks/usePatientCard';
import { useManufacturerStatus } from '../../../../hooks/useManufacturerStatus';
import { Manufacturer, manufacturerBrandName } from '../../../../utils/manufacturer';
import type { ActionRunnerProps } from '../types';

const HEADER_TITLE = 'Link / unlink Spark case';

function errorMessageOf(err: unknown, fallback: string): string {
    return err instanceof Error ? err.message : fallback;
}

function ActionHeader({ onBack }: { onBack: () => void }) {
    return (
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 flex-shrink-0">
            <button
                onClick={onBack}
                className="flex items-center gap-1.5 text-gray-500 hover:text-gray-800 transition-colors text-sm"
            >
                <ArrowLeftIcon className="w-3.5 h-3.5" />
                Back to actions
            </button>
            <div className="text-sm font-semibold text-gray-800">{HEADER_TITLE}</div>
            <div className="w-[120px]" />
        </div>
    );
}

function SparkCaseLinkAction({ patientId, patientName, onBack, onDataChange }: ActionRunnerProps) {
    const {
        data: card,
        isLoading: isCardLoading,
        refetch: refetchCard
    } = usePatientCard(patientId);
    const {
        manufacturer,
        automationReady,
        isLoading: isStatusLoading
    } = useManufacturerStatus(patientId);

    const [error, setError] = useState<string | null>(null);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const [isSearching, setIsSearching] = useState(false);
    const [matches, setMatches] = useState<SparkCaseMatch[] | null>(null);

    const linkedCaseId = card?.treatment?.manufacturerCaseId ?? null;
    const manufacturerLink = card?.treatment?.manufacturerLink ?? null;
    const sparkAcct = card?.practice?.sparkAcct ?? null;
    // This action drives the Spark portal specifically, so a case belonging to
    // another manufacturer can't use it at all — the backend rejects it too.
    const isOtherManufacturer = manufacturer !== null && manufacturer !== Manufacturer.Spark;
    // Check the manufacturer explicitly rather than leaning on automationReady:
    // that flag is only Spark-exclusive while Spark is the sole automated
    // manufacturer, so an Invisalign client would silently open this Spark UI.
    const isSparkConfigured = automationReady && !isOtherManufacturer;

    const handleUnlink = useCallback(async () => {
        if (isSubmitting) return;
        setIsSubmitting(true);
        setError(null);
        setSuccessMessage(null);
        try {
            await unlinkSparkCase(patientId);
            await refetchCard();
            onDataChange?.();
            setSuccessMessage('Spark case unlinked from this patient.');
        } catch (err: unknown) {
            setError(errorMessageOf(err, 'Failed to unlink Spark case'));
        } finally {
            setIsSubmitting(false);
        }
    }, [isSubmitting, patientId, refetchCard, onDataChange]);

    const handleLink = useCallback(
        async (caseId: number) => {
            if (isSubmitting) return;
            setIsSubmitting(true);
            setError(null);
            setSuccessMessage(null);
            try {
                const result = await linkSparkCase(patientId, caseId);
                await refetchCard();
                onDataChange?.();
                setMatches(null);
                setSuccessMessage(`Linked Spark case ${result.caseId}.`);
            } catch (err: unknown) {
                setError(errorMessageOf(err, 'Failed to link Spark case'));
            } finally {
                setIsSubmitting(false);
            }
        },
        [isSubmitting, patientId, refetchCard, onDataChange]
    );

    const handleSearch = useCallback(
        async (firstName: string, lastName: string) => {
            if (isSearching) return;
            setIsSearching(true);
            setError(null);
            setSuccessMessage(null);
            try {
                const result = await searchSparkCases(patientId, { firstName, lastName });
                setMatches(result.matches);
            } catch (err: unknown) {
                setError(errorMessageOf(err, 'Failed to search Spark cases'));
            } finally {
                setIsSearching(false);
            }
        },
        [isSearching, patientId]
    );

    const isBusy = isCardLoading || isStatusLoading;

    return (
        <div className="flex flex-col flex-1 min-h-0">
            <ActionHeader onBack={onBack} />

            <div className="flex-1 min-h-0 overflow-auto p-6">
                <div className="max-w-xl mx-auto">
                    <div className="p-4 rounded-xl border border-gray-200 bg-gray-50">
                        <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
                            Patient
                        </div>
                        <div className="text-base font-semibold text-gray-900">{patientName}</div>
                    </div>

                    {successMessage && (
                        <div className="mt-4 flex items-start gap-3 p-4 rounded-xl border border-green-200 bg-green-50">
                            <CheckCircleIcon className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                            <div className="text-sm text-green-800">{successMessage}</div>
                        </div>
                    )}

                    {error && (
                        <div className="mt-4 flex items-start gap-3 p-4 rounded-xl border border-red-200 bg-red-50">
                            <ExclamationTriangleIcon className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                            <div className="text-sm text-red-800">{error}</div>
                        </div>
                    )}

                    {isBusy ? (
                        <div className="mt-6 flex items-center gap-2 text-sm text-gray-500">
                            <div className="w-3.5 h-3.5 rounded-full border-2 border-gray-300 border-t-transparent animate-spin" />
                            Loading Spark status…
                        </div>
                    ) : linkedCaseId ? (
                        <>
                            {isOtherManufacturer && (
                                <div className="mt-6 flex items-start gap-3 p-4 rounded-xl border border-yellow-300 bg-yellow-50">
                                    <ExclamationTriangleIcon className="w-5 h-5 text-yellow-700 flex-shrink-0 mt-0.5" />
                                    <div className="text-sm text-yellow-900">
                                        <div className="font-semibold mb-1">
                                            Aligner Preference no longer matches this case.
                                        </div>
                                        <p>
                                            {`This patient's Aligner Preference is now ${manufacturerBrandName(manufacturer)}, but a Spark case is still linked below. Unlink it if it's no longer needed.`}
                                        </p>
                                    </div>
                                </div>
                            )}
                            <LinkedState
                                caseId={linkedCaseId}
                                manufacturerLink={manufacturerLink}
                                isSubmitting={isSubmitting}
                                onUnlink={handleUnlink}
                            />
                        </>
                    ) : !isSparkConfigured ? (
                        <div className="mt-6 flex items-start gap-3 p-4 rounded-xl border border-yellow-300 bg-yellow-50">
                            <ExclamationTriangleIcon className="w-5 h-5 text-yellow-700 flex-shrink-0 mt-0.5" />
                            <div className="text-sm text-yellow-900">
                                <div className="font-semibold mb-1">
                                    {isOtherManufacturer
                                        ? 'This is not a Spark case.'
                                        : 'Spark is not available for this practice.'}
                                </div>
                                <p>
                                    {isOtherManufacturer
                                        ? `This patient's Aligner Preference is ${manufacturerBrandName(manufacturer)}, which has no guided integration — the case is handled manually.`
                                        : sparkAcct
                                          ? 'The practice has a Spark account, but Spark is not currently usable (token not provisioned). Linking is disabled.'
                                          : 'This practice has no Spark account configured, so a Spark case cannot be linked.'}
                                </p>
                            </div>
                        </div>
                    ) : (
                        <UnlinkedState
                            defaultFirstName={card?.firstName ?? ''}
                            defaultLastName={card?.lastName ?? ''}
                            matches={matches}
                            isSearching={isSearching}
                            isSubmitting={isSubmitting}
                            onSearch={handleSearch}
                            onLink={handleLink}
                        />
                    )}
                </div>
            </div>
        </div>
    );
}

interface LinkedStateProps {
    caseId: string;
    manufacturerLink: string | null;
    isSubmitting: boolean;
    onUnlink: () => void;
}

function LinkedState({ caseId, manufacturerLink, isSubmitting, onUnlink }: LinkedStateProps) {
    return (
        <>
            <div className="mt-6 p-4 rounded-xl border border-gray-200 bg-white">
                <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
                    Linked Spark case
                </div>
                <div className="text-base font-semibold text-gray-900 font-mono">{caseId}</div>
                {manufacturerLink && (
                    <a
                        href={manufacturerLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mt-2 inline-flex items-center gap-1.5 text-sm text-blue-600 hover:underline"
                    >
                        <LinkIcon className="w-4 h-4" />
                        Open in Spark
                    </a>
                )}
            </div>

            <div className="mt-4 text-sm text-gray-600">
                Unlinking detaches this Spark case from the patient in our system. It does not
                delete the case in Spark, and it can be re-linked later.
            </div>

            <div className="mt-6 flex justify-end items-center gap-3">
                <button
                    onClick={onUnlink}
                    disabled={isSubmitting}
                    className="flex items-center gap-2 bg-red-600 text-white rounded-lg px-4 py-2 text-sm font-semibold hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    {isSubmitting ? (
                        <div className="w-3.5 h-3.5 rounded-full border-2 border-white border-t-transparent animate-spin" />
                    ) : (
                        <LinkSlashIcon className="w-4 h-4" />
                    )}
                    {isSubmitting ? 'Unlinking…' : 'Unlink Spark case'}
                </button>
            </div>
        </>
    );
}

interface UnlinkedStateProps {
    defaultFirstName: string;
    defaultLastName: string;
    matches: SparkCaseMatch[] | null;
    isSearching: boolean;
    isSubmitting: boolean;
    onSearch: (firstName: string, lastName: string) => void;
    onLink: (caseId: number) => void;
}

function UnlinkedState({
    defaultFirstName,
    defaultLastName,
    matches,
    isSearching,
    isSubmitting,
    onSearch,
    onLink
}: UnlinkedStateProps) {
    const [firstName, setFirstName] = useState(defaultFirstName);
    const [lastName, setLastName] = useState(defaultLastName);

    const canSearch = lastName.trim() !== '' && !isSearching && !isSubmitting;

    return (
        <>
            <div className="mt-6 text-sm text-gray-600">
                No Spark case is linked to this patient. Search Spark by name to find a case to
                link. The patient&apos;s name is prefilled — adjust it if the Spark case uses a
                different name.
            </div>

            <div className="mt-6 grid grid-cols-2 gap-3">
                <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">
                        First name
                    </label>
                    <input
                        type="text"
                        value={firstName}
                        onChange={(e) => setFirstName(e.target.value)}
                        placeholder="First name"
                        autoComplete="off"
                        spellCheck={false}
                        className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 text-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 transition-colors duration-200"
                    />
                </div>
                <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">
                        Last name
                    </label>
                    <input
                        type="text"
                        value={lastName}
                        onChange={(e) => setLastName(e.target.value)}
                        placeholder="Last name"
                        autoComplete="off"
                        spellCheck={false}
                        className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 text-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 transition-colors duration-200"
                    />
                </div>
            </div>

            <div className="mt-4">
                <button
                    onClick={() => onSearch(firstName.trim(), lastName.trim())}
                    disabled={!canSearch}
                    className="flex items-center gap-2 bg-gray-900 text-white rounded-lg px-4 py-2 text-sm font-semibold hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    {isSearching ? (
                        <div className="w-3.5 h-3.5 rounded-full border-2 border-white border-t-transparent animate-spin" />
                    ) : (
                        <MagnifyingGlassIcon className="w-4 h-4" />
                    )}
                    {isSearching ? 'Searching…' : 'Search Spark'}
                </button>
            </div>

            {matches && (
                <div className="mt-4">
                    {matches.length === 0 ? (
                        <div className="text-sm text-gray-400 italic">
                            No matching Spark cases found for that name.
                        </div>
                    ) : (
                        <div className="flex flex-col gap-2">
                            {matches.map((match) => (
                                <SparkMatchRow
                                    key={match.caseId}
                                    match={match}
                                    disabled={isSubmitting}
                                    onLink={() => onLink(match.caseId)}
                                />
                            ))}
                        </div>
                    )}
                </div>
            )}
        </>
    );
}

interface SparkMatchRowProps {
    match: SparkCaseMatch;
    disabled: boolean;
    onLink: () => void;
}

function SparkMatchRow({ match, disabled, onLink }: SparkMatchRowProps) {
    const name = [match.firstName, match.lastName].filter(Boolean).join(' ') || 'Unknown name';
    return (
        <div className="flex items-center justify-between gap-3 p-3 rounded-xl border border-gray-200 bg-white">
            <div className="min-w-0">
                <div className="text-sm font-semibold text-gray-900 truncate">{name}</div>
                <div className="text-xs text-gray-500">
                    Case <span className="font-mono">{match.caseId}</span> · {match.status}
                </div>
            </div>
            <button
                onClick={onLink}
                disabled={disabled}
                className="flex items-center gap-1.5 bg-gray-900 text-white rounded-lg px-3 py-2 text-sm font-semibold hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
            >
                <LinkIcon className="w-4 h-4" />
                Link
            </button>
        </div>
    );
}

export { SparkCaseLinkAction };
